import { getCategoryProfile } from "../category-profiles";
import { createDefaultIntakeData, createDraftId, FIXED_UPSELL_COMMISSION_RATE, INTAKE_STEPS, isDepositContractType, isUpsellContractType, LOCAL_STORAGE_KEY, normalizeContractOfferForCategory, normalizeContractTypeForCategory, parseOfferedContractTypes, sanitizeUpsellRate } from "../form-schema";
import { getDefaultUpsellRateForContext, type FormConfigVersion } from "../form-config";
import type { DraftState, ProductOption } from "../types";
import { fieldValueToString } from "./value-utils";

export type ProductPricingMode = "unselected" | "free" | "deposit" | "settlement";

export const nowIso = () => new Date().toISOString();

export function createInitialDraft(): DraftState {
  const createdAt = nowIso();
  return {
    draftId: createDraftId(),
    data: createDefaultIntakeData(),
    files: [],
    currentStep: 0,
    createdAt,
    updatedAt: createdAt,
  };
}

export function getProductPricingMode(contractType: string): ProductPricingMode {
  if (contractType === "free_reservation_upsell") return "free";
  if (isDepositContractType(contractType)) return "deposit";
  if (!contractType) return "unselected";
  return "settlement";
}

export function isNumericLikePrice(value: string): boolean {
  return /\d/.test(value) && /^[\d,\s원₩.-]+$/.test(value);
}

export function normalizeProductOptionPricing(product: ProductOption, pricingMode: ProductPricingMode): ProductOption {
  const legacyPrice = fieldValueToString(product.depositOrSettlementPrice).trim();
  const depositAmount = fieldValueToString(product.depositAmount).trim() || (pricingMode === "deposit" && isNumericLikePrice(legacyPrice) ? legacyPrice : "");
  const settlementPrice = fieldValueToString(product.settlementPrice).trim() || (pricingMode === "settlement" && isNumericLikePrice(legacyPrice) ? legacyPrice : "");
  const depositOrSettlementPrice = pricingMode === "free"
    ? "무료예약"
    : pricingMode === "deposit"
      ? depositAmount
      : pricingMode === "settlement"
        ? settlementPrice
        : legacyPrice;

  return {
    ...product,
    freeReservationLabel: "무료예약",
    depositAmount,
    settlementPrice,
    depositOrSettlementPrice,
    detailDescription: fieldValueToString(product.detailDescription),
    effectDuration: fieldValueToString(product.effectDuration),
    recommendedTargets: fieldValueToString(product.recommendedTargets),
    productPrecautions: fieldValueToString(product.productPrecautions),
  };
}

function normalizeReservationUnit(value: string): string {
  const allowedUnits = new Set(["15분", "30분", "1시간", "1시간 30분", "2시간"]);
  const normalized = value.replace(/\s*단위$/, "").trim();
  return allowedUnits.has(normalized) ? normalized : createDefaultIntakeData().reservation.reservationUnit;
}

function normalizeReservationDefaults(reservation: DraftState["data"]["reservation"]): DraftState["data"]["reservation"] {
  const defaults = createDefaultIntakeData().reservation;
  return {
    ...reservation,
    bookableStartOffsetDays: fieldValueToString(reservation.bookableStartOffsetDays).trim() || defaults.bookableStartOffsetDays,
    bookableWindowMonths: fieldValueToString(reservation.bookableWindowMonths).trim() || defaults.bookableWindowMonths,
    businessHours: fieldValueToString(reservation.businessHours).trim() || defaults.businessHours,
    reservationHours: fieldValueToString(reservation.reservationHours).trim() || defaults.reservationHours,
    reservationUnit: normalizeReservationUnit(fieldValueToString(reservation.reservationUnit).trim()),
    confirmationMode: fieldValueToString(reservation.confirmationMode).trim() || defaults.confirmationMode,
    communicationChannel: fieldValueToString(reservation.communicationChannel).trim() || defaults.communicationChannel,
  };
}

function synchronizeTaxInvoiceContact(company: DraftState["data"]["company"]): DraftState["data"]["company"] {
  if (!company.useContactInfoForTaxInvoice) return company;
  return {
    ...company,
    taxInvoiceContactName: company.contactName,
    taxInvoiceContactPhone: company.contactPhone,
    taxInvoiceContactEmail: company.contactEmail,
  };
}

function normalizeGoogleIntegrationValue(value: string): string {
  return value === "no" ? "no" : "yes";
}

function normalizeExclusiveBenefitStatus(value: string): string {
  return ["없음", "준비/상담 중"].includes(value) ? value : "있음";
}

function normalizeContentDefaults(content: DraftState["data"]["content"]): DraftState["data"]["content"] {
  return {
    ...content,
    creatripExclusiveBenefitStatus: normalizeExclusiveBenefitStatus(fieldValueToString(content.creatripExclusiveBenefitStatus).trim()),
    googleReviewIntegration: normalizeGoogleIntegrationValue(fieldValueToString(content.googleReviewIntegration).trim()),
    googleBookingIntegration: normalizeGoogleIntegrationValue(fieldValueToString(content.googleBookingIntegration).trim()),
  };
}

function normalizePolicyDefaults(policy: DraftState["data"]["policy"]): DraftState["data"]["policy"] {
  const defaults = createDefaultIntakeData().policy;
  const consultationFeePolicy = policy.consultationFeePolicy === "charged" ? "charged" : defaults.consultationFeePolicy;
  return {
    ...policy,
    cancellationRefundPolicy: fieldValueToString(policy.cancellationRefundPolicy).trim() || defaults.cancellationRefundPolicy,
    visitFlow: fieldValueToString(policy.visitFlow).trim() || defaults.visitFlow,
    bookingQuestionChecklist: fieldValueToString(policy.bookingQuestionChecklist).trim() || defaults.bookingQuestionChecklist,
    consultationFeePolicy,
    consultationFeeAmount: fieldValueToString(policy.consultationFeeAmount).trim(),
    minorPolicy: fieldValueToString(policy.minorPolicy).trim() || defaults.minorPolicy,
    passportNeeded: fieldValueToString(policy.passportNeeded).trim() || defaults.passportNeeded,
    healthRestrictions: fieldValueToString(policy.healthRestrictions).trim() || defaults.healthRestrictions,
  };
}

function normalizeDoctorProfiles(doctors: DraftState["data"]["doctors"]): DraftState["data"]["doctors"] {
  return doctors.map((doctor) => {
    const record = doctor as unknown as Record<string, unknown>;
    const legacySections = [
      ["추가 소개", fieldValueToString(record.description).trim()],
      ["이력", fieldValueToString(record.career).trim()],
      ["학력", fieldValueToString(record.education).trim()],
      ["학회", fieldValueToString(record.academicSocieties).trim()],
    ]
      .filter(([, value]) => value)
      .map(([label, value]) => `${label}\n${value}`)
      .join("\n\n");

    return {
      name: fieldValueToString(record.name),
      role: fieldValueToString(record.role),
      profileText: fieldValueToString(record.profileText).trim() || legacySections,
      referenceUrl: fieldValueToString(record.referenceUrl),
    };
  });
}

function normalizeProcedureDetails(procedures: DraftState["data"]["procedures"]): DraftState["data"]["procedures"] {
  return procedures.map((procedure) => {
    const record = procedure as unknown as Record<string, unknown>;
    return {
      treatmentName: fieldValueToString(record.treatmentName),
      description: fieldValueToString(record.description),
      duration: fieldValueToString(record.duration),
      effectDuration: fieldValueToString(record.effectDuration),
    };
  });
}

export function normalizeIntakeData(data: DraftState["data"], config?: FormConfigVersion): DraftState["data"] {
  const profile = getCategoryProfile(data.category?.categoryId);
  const parsedOfferedContractTypes = parseOfferedContractTypes(data.contract.offeredContractTypes);
  const offeredUpsellCommissionRate = sanitizeUpsellRate(data.contract.offeredUpsellCommissionRate);
  const categoryContractType = profile
    ? normalizeContractTypeForCategory(data.contract.contractType, profile.id, profile.domain, config)
    : data.contract.contractType;
  const compatibleOfferedContractTypes = profile
    ? normalizeContractOfferForCategory(parsedOfferedContractTypes, profile.id, profile.domain, config)
    : parsedOfferedContractTypes;
  const offeredContractTypes = parsedOfferedContractTypes.length && !compatibleOfferedContractTypes.length
    ? [categoryContractType]
    : compatibleOfferedContractTypes;
  const contractType = offeredContractTypes.length && !offeredContractTypes.includes(categoryContractType)
    ? offeredContractTypes[0]
    : categoryContractType;
  const pricingMode = getProductPricingMode(contractType);
  return {
    ...data,
    category: profile ? { domain: profile.domain, categoryId: profile.id, label: profile.label } : createDefaultIntakeData().category,
    company: synchronizeTaxInvoiceContact(data.company),
    reservation: normalizeReservationDefaults(data.reservation),
    content: normalizeContentDefaults(data.content),
    policy: normalizePolicyDefaults(data.policy),
    contract: {
      ...data.contract,
      contractType,
      offeredContractTypes: offeredContractTypes.join("\n"),
      offeredUpsellCommissionRate,
      // 파트너 기입값 우선, 비어 있거나 형식이 틀리면 오퍼 기본값 → 카테고리 기본값 → 10% 순으로 채운다.
      upsellCommissionRate: isUpsellContractType(contractType)
        ? (
          sanitizeUpsellRate(data.contract.upsellCommissionRate)
          || offeredUpsellCommissionRate
          || (profile ? sanitizeUpsellRate(getDefaultUpsellRateForContext({ categoryId: profile.id, domain: profile.domain }, config)) : "")
          || FIXED_UPSELL_COMMISSION_RATE
        )
        : "",
    },
    products: data.products.map((product) => normalizeProductOptionPricing(product, pricingMode)),
    doctors: normalizeDoctorProfiles(data.doctors),
    procedures: normalizeProcedureDetails(data.procedures),
  };
}

export function normalizeDraftShape(parsed: Partial<DraftState> | null | undefined, config?: FormConfigVersion): DraftState | null {
  if (!parsed?.draftId || !parsed?.data) return null;
  const defaults = createDefaultIntakeData();
  const parsedData = parsed.data as Partial<DraftState["data"]>;
  return {
    ...createInitialDraft(),
    ...parsed,
    data: normalizeIntakeData({
      ...defaults,
      ...parsedData,
      category: { ...defaults.category, ...parsedData.category },
      company: { ...defaults.company, ...parsedData.company },
      reservation: { ...defaults.reservation, ...parsedData.reservation },
      contract: { ...defaults.contract, ...parsedData.contract },
      content: { ...defaults.content, ...parsedData.content },
      language: { ...defaults.language, ...parsedData.language },
      policy: { ...defaults.policy, ...parsedData.policy },
      documents: { ...defaults.documents, ...parsedData.documents },
      products: Array.isArray(parsedData.products) && parsedData.products.length ? parsedData.products : defaults.products,
      doctors: Array.isArray(parsedData.doctors) && parsedData.doctors.length ? parsedData.doctors : defaults.doctors,
      procedures: Array.isArray(parsedData.procedures) && parsedData.procedures.length ? parsedData.procedures : defaults.procedures,
    }, config),
    files: Array.isArray(parsed.files) ? parsed.files : [],
    currentStep: typeof parsed.currentStep === "number" && Number.isInteger(parsed.currentStep) ? Math.min(Math.max(parsed.currentStep, 0), INTAKE_STEPS.length - 1) : 0,
  };
}

export function loadStoredDraft(): DraftState | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return null;
    return normalizeDraftShape(JSON.parse(raw) as Partial<DraftState>);
  } catch {
    return null;
  }
}
